// Client-side VOD conversion (ADR-0004). Owner: WP03-convert. Public surface used by the player (WP03); keep stable.
// Mediabunny + @mediabunny/ac3 in a Web Worker (./worker.ts): read /media/{session}/{resource} via Range, copy video,
// decode AC-3/E-AC-3 (WASM) → AAC (WebCodecs) → fragmented MP4 → MSE. Never re-encodes video.
// This module is tiny and loads nothing heavy: mediabunny and the WASM live only in the worker bundle.
import type { AudioPlan, FromWorker, Opened, ToWorker } from './worker.ts';

export type ProbeResult = {
  container: string;                         // 'mp4' | 'mov' | 'mkv' | 'webm' | 'mpegts' | ...
  durationSec: number | null;
  video: { codec: string; codecString: string | null; width: number | null; height: number | null } | null;
  audio: { codec: string; codecString: string | null; channels: number | null; language: string | null }[];
};
export type Decision = { kind: 'native' } | { kind: 'convert'; reason: string } | { kind: 'unsupported'; reason: string };
export type ConvertError = { code: 'UNSUPPORTED_MEDIA' | 'CONVERSION_FAILED'; reason: string };
export type ConvertStats = { bufferedAheadSec: number; convertedSec: number; speed: number };
export type ConvertedPlayback = { seek(sec: number): Promise<void>; destroy(): void };

const AAC = 'mp4a.40.2';
// PLAY04 budget per tile: convert at most 60 s (and 64 MiB) ahead, top up once below 30 s ahead; keep ≤60 s behind.
// Conversion runs ~100x real time, so the worker is given an exact media-time limit rather than pause/resume.
const AHEAD_PAUSE_SEC = 60;
const AHEAD_RESUME_SEC = 30;
const BACK_KEEP_SEC = 60;
const BYTE_BUDGET = 64 * 1024 * 1024;
const TICK_MS = 1000;

const LABELS: Record<string, string> = {
  avc: 'H.264', hevc: 'HEVC', vp8: 'VP8', vp9: 'VP9', av1: 'AV1', mpeg4: 'MPEG-4 Part 2', mjpeg: 'Motion JPEG',
  ac3: 'AC-3', eac3: 'E-AC-3', dts: 'DTS', aac: 'AAC', mp3: 'MP3', mp2: 'MP2', opus: 'Opus', vorbis: 'Vorbis', flac: 'FLAC',
  mp4: 'MP4', mov: 'MOV', mkv: 'MKV', webm: 'WebM', avi: 'AVI', mpegts: 'MPEG-TS',
};
const label = (id: string) => LABELS[id] ?? id.toUpperCase();
// Containers the browser may play progressively as-is; everything else is remuxed.
const NATIVE_MIME: Record<string, string> = { mp4: 'video/mp4', mov: 'video/quicktime', webm: 'video/webm' };
// Audio the worker can decode: AC-3/E-AC-3 in WASM, the rest with WebCodecs AudioDecoder.
const decodable = (codec: string) => ['ac3', 'eac3', 'aac', 'mp3', 'opus', 'vorbis', 'flac'].includes(codec) || codec.startsWith('pcm-');

type MediaSourceCtor = typeof MediaSource;
const mediaSourceCtor = (): MediaSourceCtor | undefined =>
  (globalThis as { ManagedMediaSource?: MediaSourceCtor }).ManagedMediaSource ?? globalThis.MediaSource;
function mse(type: string): boolean {
  try { return mediaSourceCtor()?.isTypeSupported(type) ?? false; } catch { return false; }
}
const canPlay = (type: string) => (typeof document === 'undefined' ? '' : document.createElement('video').canPlayType(type));
const spawn = () => new Worker(new URL('./worker.ts', import.meta.url), { type: 'module' });
const absolute = (url: string) => new URL(url, location.href).href;

/** What this browser can convert (sent as ClientCapabilities.convert). Must not load WASM. */
export function conversionSupport(): { audio: string[]; containers: string[] } {
  // ponytail: presence checks only (the API is sync); the worker verifies AAC/Opus encodability on open and fails
  // with UNSUPPORTED_MEDIA if neither encodes.
  const ok = typeof AudioEncoder === 'function' && typeof Worker === 'function' && typeof WebAssembly === 'object'
    && mse('video/mp4; codecs="avc1.42E01E"')
    && (mse(`audio/mp4; codecs="${AAC}"`) || mse('audio/mp4; codecs="opus"'));
  return ok ? { audio: ['ac3', 'eac3'], containers: ['mkv', 'mov', 'mp4'] } : { audio: [], containers: [] };
}

/** Reads container headers via Range requests only. audio[0] is the track that plays (default/primary). */
export function probeSource(url: string, signal?: AbortSignal): Promise<ProbeResult> {
  return new Promise((resolve, reject) => {
    const worker = spawn();
    const done = () => { worker.terminate(); signal?.removeEventListener('abort', abort); };
    const abort = () => { done(); reject(signal?.reason ?? new DOMException('Aborted', 'AbortError')); };
    if (signal?.aborted) return abort();
    signal?.addEventListener('abort', abort, { once: true });
    worker.onmessage = ({ data: m }: MessageEvent<FromWorker>) => {
      done();
      if (m.type === 'probed') resolve(m.probe);
      else reject(new Error(m.type === 'error' ? m.reason : `unexpected ${m.type}`));
    };
    worker.onerror = e => { done(); reject(new Error(`Converter worker failed to load: ${e.message || 'unknown error'}`)); };
    worker.postMessage({ type: 'probe', url: absolute(url) } satisfies ToWorker);
  });
}

/** Native if MSE/canPlayType can play the tracks as-is; convert if audio/container is convertible; else unsupported with a specific reason. */
export function decide(probe: ProbeResult): Decision {
  const c = label(probe.container);
  const v = probe.video;
  const a = probe.audio[0];
  const unsupported = (reason: string): Decision => ({ kind: 'unsupported', reason });
  if (!v) return unsupported(probe.container === 'unknown' ? 'This file format is not recognized.' : `No video track was found in this ${c} file.`);
  const nativeMime = NATIVE_MIME[probe.container];
  if (nativeMime && v.codecString && (!a || a.codecString)) {
    const codecs = [v.codecString, a?.codecString].filter(Boolean).join(', ');
    if (canPlay(`${nativeMime}; codecs="${codecs}"`) === 'probably') return { kind: 'native' };
  }
  if (!mediaSourceCtor()) return unsupported(`${c} files with ${label(a?.codec ?? v.codec)} need MediaSource, which this browser lacks.`);
  if (!v.codecString || !mse(`video/mp4; codecs="${v.codecString}"`)) return unsupported(`${label(v.codec)} video (${c}) cannot be played in this browser.`);
  if (!a || (a.codecString && mse(`audio/mp4; codecs="${a.codecString}"`))) return { kind: 'convert', reason: `${c} remuxed to fragmented MP4` };
  if (!decodable(a.codec)) return unsupported(`${label(a.codec)} audio (${c}) cannot be played in this browser.`);
  if (!conversionSupport().audio.length) return unsupported(`${label(a.codec)} audio needs in-browser conversion, which this browser does not support.`);
  return { kind: 'convert', reason: `${label(a.codec)} audio converted to AAC` };
}

type Op = { kind: 'append'; data: Uint8Array<ArrayBuffer>; t?: number } | { kind: 'clear' } | { kind: 'remove'; to: number } | { kind: 'offset'; value: number };

function pickAudio(o: Opened): { plan: AudioPlan; codec: string | null } | null {
  const a = o.audio;
  if (!a) return { plan: 'none', codec: null };
  if (a.codecString && mse(`audio/mp4; codecs="${a.codecString}"`)) return { plan: 'copy', codec: a.codecString };
  if (o.encodable.aac && mse(`audio/mp4; codecs="${AAC}"`)) return { plan: 'aac', codec: AAC };
  if (o.encodable.opus && mse('audio/mp4; codecs="opus"')) return { plan: 'opus', codec: 'opus' };
  return null;
}

function rangeAt(ranges: TimeRanges, t: number): number {
  for (let i = 0; i < ranges.length; i++) if (ranges.start(i) <= t + 0.1 && t < ranges.end(i)) return i;
  return -1;
}

/** Attaches a MediaSource to `video` and starts converting from startSec. Caller owns play()/pause().
 * Resolves once the source is open and conversion has started (or immediately after onError on failure). */
export async function attachConverted(video: HTMLVideoElement, url: string, opts: { startSec?: number; signal?: AbortSignal; onError(e: ConvertError): void; onStats?(s: ConvertStats): void }): Promise<ConvertedPlayback> {
  const { signal, onError, onStats } = opts;
  const startSec = Math.max(0, opts.startSec ?? 0);
  const worker = spawn();
  const send = (m: ToWorker) => worker.postMessage(m);
  let dead = false;
  let stop: (v: null) => void = () => {};
  const stopped = new Promise<null>(resolve => { stop = resolve; });
  let ms: MediaSource | null = null;
  let sb: SourceBuffer | null = null;
  let objectUrl = '';
  let plan: AudioPlan = 'none';
  let gen = 0;
  let genFloor = startSec; // media time the current generation starts at (its key frame, once known)
  let convertedEnd = startSec; // media time of the newest segment of the current generation
  let fresh = true; // no media of the current generation appended yet: video.buffered still shows the old one
  let finished = false; // current generation reached the end of the file
  let limit = 0; // media time the worker may convert up to
  let blocked = false; // QuotaExceededError: wait for the next tick to trim and retry
  let segments: { t: number; bytes: number }[] = []; // appended media, for the byte budget
  let stats = { convertedSec: 0, speed: 0 };
  let ticker: ReturnType<typeof setInterval> | undefined;
  let opened: (o: Opened) => void = () => {};
  const queue: Op[] = [];

  const teardown = () => {
    if (dead) return;
    dead = true;
    clearInterval(ticker);
    worker.terminate(); // aborts the worker's in-flight Range fetches
    stop(null);
    video.removeEventListener('seeking', onSeeking);
    signal?.removeEventListener('abort', teardown);
    if (ms?.readyState === 'open' && !sb?.updating) try { ms.endOfStream(); } catch { /* already closing */ }
    if (objectUrl && video.getAttribute('src') === objectUrl) {
      video.removeAttribute('src');
      video.load(); // detaches and closes the MediaSource
    }
    if (objectUrl) URL.revokeObjectURL(objectUrl);
  };
  const fail = (code: ConvertError['code'], reason: string) => {
    if (dead) return;
    teardown();
    onError({ code, reason });
  };
  const raise = (until: number) => {
    if (until <= limit + 1) return;
    limit = until;
    send({ type: 'limit', until });
  };

  const pump = () => {
    if (dead || !ms || !sb || sb.updating || blocked) return;
    const op = queue.shift();
    if (!op) {
      if (finished && ms.readyState === 'open') ms.endOfStream();
      return;
    }
    try {
      switch (op.kind) {
        case 'clear':
          if (ms.readyState === 'open') sb.abort();
          segments = [];
          if (sb.buffered.length) sb.remove(0, Infinity);
          else pump();
          return;
        case 'offset':
          sb.timestampOffset = op.value;
          return pump();
        case 'remove':
          segments = segments.filter(s => s.t >= op.to);
          sb.remove(0, op.to);
          return;
        case 'append':
          sb.appendBuffer(op.data);
          if (op.t !== undefined) {
            segments.push({ t: op.t, bytes: op.data.byteLength });
            fresh = false;
          }
          return;
      }
    } catch (e) {
      if (op.kind === 'append' && e instanceof DOMException && e.name === 'QuotaExceededError') {
        queue.unshift(op);
        blocked = true;
        return;
      }
      fail('CONVERSION_FAILED', `The browser rejected the converted media: ${e instanceof Error ? e.message : String(e)}`);
    }
  };

  const restart = (sec: number) => {
    gen++;
    genFloor = convertedEnd = sec;
    fresh = true;
    finished = blocked = false;
    limit = sec + AHEAD_PAUSE_SEC;
    queue.length = 0;
    queue.push({ kind: 'clear' });
    send({ type: 'start', gen, startSec: sec, audio: plan, until: limit });
    pump();
  };

  // Every seek lands here (seek(), native controls, the player). Inside buffered data or just ahead of the
  // conversion: wait. Anywhere else: restart the pipeline at the new position. Until the current generation has
  // appended media, video.buffered still describes the previous one (its removal is pending), so ignore it.
  function onSeeking() {
    const t = video.currentTime;
    if (!fresh && rangeAt(video.buffered, t) >= 0) return;
    const lo = !fresh && video.buffered.length ? video.buffered.start(0) : genFloor;
    if (t >= lo - 0.5 && t <= convertedEnd + 10) return raise(t + AHEAD_PAUSE_SEC);
    restart(t);
  }

  const tick = () => {
    if (dead || !sb) return;
    const t = video.currentTime;
    const i = rangeAt(video.buffered, t);
    const ahead = i < 0 ? 0 : video.buffered.end(i) - t;
    const bytes = segments.reduce((n, s) => n + s.bytes, 0);
    const keep = bytes >= BYTE_BUDGET ? 10 : BACK_KEEP_SEC;
    const back = sb.buffered.length ? sb.buffered.start(0) : t;
    if (t - back > keep + 5 && !queue.some(op => op.kind === 'remove')) queue.unshift({ kind: 'remove', to: t - keep });
    blocked = false;
    if (ahead < AHEAD_RESUME_SEC) {
      // High-bitrate titles: shrink the window so buffered bytes stay inside the budget.
      const span = segments.length > 1 ? segments.at(-1)!.t - segments[0]!.t : 0;
      const room = span > 1 ? ((BYTE_BUDGET - bytes) * span) / bytes : Infinity;
      raise(t + Math.min(AHEAD_PAUSE_SEC, ahead + Math.max(0, room)));
    }
    onStats?.({ bufferedAheadSec: ahead, ...stats });
    pump();
  };

  worker.onmessage = ({ data: m }: MessageEvent<FromWorker>) => {
    if (dead) return;
    switch (m.type) {
      case 'opened': return opened(m);
      case 'error': return fail(m.code, m.reason);
      case 'init':
        if (m.gen !== gen) return;
        genFloor = m.offset;
        queue.push({ kind: 'offset', value: m.offset }, { kind: 'append', data: m.data as Uint8Array<ArrayBuffer> });
        break;
      case 'segment':
        if (m.gen !== gen) return;
        convertedEnd = m.start;
        stats = { convertedSec: m.convertedSec, speed: m.speed };
        queue.push({ kind: 'append', data: m.data as Uint8Array<ArrayBuffer>, t: m.start });
        break;
      case 'done':
        if (m.gen === gen) finished = true;
        break;
    }
    pump();
  };
  worker.onerror = e => fail('CONVERSION_FAILED', `Converter worker failed: ${e.message || 'unknown error'}`);

  const handle: ConvertedPlayback = {
    async seek(sec) {
      if (!dead) video.currentTime = Math.max(0, sec); // onSeeking restarts the pipeline when needed
    },
    destroy: teardown,
  };
  if (signal?.aborted) {
    teardown();
    return handle;
  }
  signal?.addEventListener('abort', teardown, { once: true });

  send({ type: 'open', url: absolute(url) });
  const info = await Promise.race([new Promise<Opened>(resolve => { opened = resolve; }), stopped]);
  if (!info) return handle;
  const audio = pickAudio(info);
  if (!audio) {
    fail('UNSUPPORTED_MEDIA', `${label(info.audio?.codec ?? 'unknown')} audio cannot be converted: this browser encodes neither AAC nor Opus for MediaSource.`);
    return handle;
  }
  plan = audio.plan;
  const MS = mediaSourceCtor();
  const mime = `video/mp4; codecs="${[info.videoCodecString, audio.codec].filter(Boolean).join(', ')}"`;
  if (!MS || !info.videoCodecString || !MS.isTypeSupported(mime)) {
    fail('UNSUPPORTED_MEDIA', `The converted stream (${mime}) cannot be played in this browser.`);
    return handle;
  }

  const source = new MS();
  ms = source;
  objectUrl = URL.createObjectURL(source);
  if (MS !== globalThis.MediaSource) video.disableRemotePlayback = true; // ManagedMediaSource needs this (or an AirPlay source)
  const open = new Promise<true>(resolve => source.addEventListener('sourceopen', () => resolve(true), { once: true }));
  video.src = objectUrl;
  if (!(await Promise.race([open, stopped]))) return handle;
  try {
    sb = source.addSourceBuffer(mime);
    if (info.durationSec) source.duration = info.durationSec; // seek bar spans the whole title, not the converted part
  } catch (e) {
    fail('UNSUPPORTED_MEDIA', `The converted stream (${mime}) cannot be played in this browser: ${e instanceof Error ? e.message : String(e)}`);
    return handle;
  }
  sb.addEventListener('updateend', pump);
  sb.addEventListener('error', () => fail('CONVERSION_FAILED', 'The browser could not decode the converted media.'));
  video.addEventListener('seeking', onSeeking);
  if (startSec > 0) video.currentTime = startSec;
  restart(startSec);
  ticker = setInterval(tick, TICK_MS);
  return handle;
}
